import React, { useContext, useCallback, useState } from 'react';
import loadable from '@loadable/component';
import PaxDispatch from '../../PaxDispatch';
import { UPDATE_ROOM, CHILD_AGE_PLACEHOLDER, BED_REQUIRED_ENUM } from '../../../Constant/constants';
import AddChildrenAge from './AddChildrenAge';
import { MinusIcon, PlusIcon } from '../../../../../../assets/svg/icons';
import WarningMessage from '../WarningMessage';
import {
  faOnAddAdult,
  faOnAddChildren,
  faOnAddRoom,
  faOnRemoveAdult,
  faOnRemoveChildren,
} from '../../../analytics-services';

import './RoomDetail.css';

const ChildPolicies = loadable(
  () => /* webpackChunkName: ChildPolicies */ import('./ChildPolicies')
);

const RoomDetail = ({
  adults,
  children,
  roomIndex,
  paxConfig,
  childBedRequired = [],
  omniture,
}) => {
  const [showModalFlag, setShowModalFlag] = useState('');
  const adultCount = adults || 0;
  const childCount = (children && children.length) || 0;
  const dispatch = useContext(PaxDispatch);
  const travellersCount = adultCount + childCount;
  const {
    maxPaxAllowed = 4,
    maxAdultAllowed = 4,
    maxChildAllowed = 4,
    minChildAgeForBedCompulsory = 7,
    childWithBed = false,
    minChildAge,
    extraBedAllowedMessage,
  } = paxConfig || {};
  const { omnitureUtils, params } = omniture;

  const handleAdultCountChange = (counter, actionType) => {
    actionType === 'add' ? faOnAddAdult(counter) : faOnRemoveAdult(counter);
    if (counter > 0) {
      dispatch({
        type: UPDATE_ROOM,
        index: roomIndex,
        room: { adults: counter, children, childBedRequired },
      });
    }
  };

  const handleChildCountChange = (counter, actionType) => {
    let updatedChildren = children || [];
    let childBedRequired = [];
    if (childWithBed) {
      if (counter > 1) {
        for (let i = 0; i < counter; i++) {
          childBedRequired.push('Yes');
        }
      } else {
        childBedRequired = [];
      }
    } else {
      for (let i = 0; i < counter; i++) {
        childBedRequired.push('No');
      }
    }

    if (counter < childCount) {
      updatedChildren = updatedChildren.slice(0, counter);
    } else {
      updatedChildren = updatedChildren.concat(
        Array(counter - childCount).fill(CHILD_AGE_PLACEHOLDER)
      );
    }
    actionType === 'add' ? faOnAddChildren(counter) : faOnRemoveChildren(counter);
    dispatch({
      type: UPDATE_ROOM,
      index: roomIndex,
      room: {
        adults,
        children: updatedChildren,
        childBedRequired,
      },
    });
  };

  const handleChildChangeCallback = useCallback(
    updatedChildren => {
      const updatedChildBeds = [];
      if (childWithBed) {
        if (updatedChildren.length) {
          if (updatedChildren.length === 1) {
            if (updatedChildren[0] < minChildAge) {
              updatedChildBeds.push('No');
            } else {
              updatedChildBeds.push('Yes');
            }
          } else {
            for (let i = 0; i < updatedChildren.length; i++) {
              updatedChildBeds.push('Yes');
            }
          }
        }
      } else {
        for (let i = 0; i < updatedChildren.length; i++) {
          updatedChildBeds.push('No');
        }
      }

      dispatch({
        type: UPDATE_ROOM,
        index: roomIndex,
        room: { adults, children: updatedChildren, childBedRequired: updatedChildBeds },
      });
    },
    [adults, children, roomIndex]
  );

  const handleBedChangeCallback = value => {
    dispatch({
      type: UPDATE_ROOM,
      index: roomIndex,
      room: { adults, children, childBedRequired: [value] },
    });
  };

  const isShowBedRequired =
    childWithBed &&
    childCount === 1 &&
    children[0] >= minChildAge &&
    children[0] < minChildAgeForBedCompulsory;

  const handleChildPolicyModal = () => {
    setShowModalFlag(!showModalFlag);
    if (!showModalFlag) {
      omnitureUtils.captureOmnitureClick('clicked_child_policies_know_more', params);
    }
  };

  const isExtraBedRequired = () => {
    if (childCount > 1) {
      let isRequired = false;
      children.forEach(child => {
        if (child >= minChildAge) {
          isRequired = true;
        }
      });
      return isRequired;
    }
    if (
      childCount === 1 &&
      (childBedRequired?.[0] === BED_REQUIRED_ENUM.Yes ||
        (children[0] !== CHILD_AGE_PLACEHOLDER && children[0] >= minChildAgeForBedCompulsory))
    ) {
      return true;
    }
    return false;
  };

  return (
    <>
      <div data-testid="pax-room-details" className="makeFlex spaceBetween">
        <div className="makeFlex column">
          <p className="appendTop10 appendBottom5">
            <span className="latoBold">Adults</span> -{' '}
            <span className="font12">Above 12 Years</span>
          </p>
          <ul className="room-detail_age guestCounter font12 darkText">
            <div className="counter-wrapper">
              <div
                data-testid="adult-decrement-counter"
                className="pointer minus-sign-wrapper"
                onClick={() => handleAdultCountChange(adultCount - 1, 'remove')}
              >
                <MinusIcon />
              </div>
              <span className="font22 darkText latoMedium" id="adultCount">
                {`${adultCount < 10 ? '0' : ''}${adultCount}`}
              </span>
              <div
                data-testid="adult-increment-counter"
                id="adult-increment-counter"
                className="pointer plus-sign-wrapper"
                onClick={
                  travellersCount < maxPaxAllowed && adultCount < maxAdultAllowed
                    ? () => handleAdultCountChange(adultCount + 1, 'add')
                    : () => {}
                }
              >
                <PlusIcon />
              </div>
            </div>
          </ul>
        </div>
        <div className="makeFlex column">
          <p className="appendTop10 appendBottom5">
            <span className="latoBold">Children</span> -{' '}
            <span className="font12">Below 12 Years</span>
          </p>
          <ul className="room-detail_age guestCounter font12 darkText">
            <div className="counter-wrapper">
              <div
                data-testid="child-decrement-counter"
                className="pointer minus-sign-wrapper"
                onClick={() => handleChildCountChange(childCount - 1, 'remove')}
              >
                <MinusIcon />
              </div>
              <span className="font22 darkText latoMedium">
                {`${childCount < 10 ? '0' : ''}${childCount}`}
              </span>
              <div
                data-testid="child-increment-counter"
                className="pointer plus-sign-wrapper"
                onClick={
                  travellersCount < maxPaxAllowed && childCount < maxChildAllowed
                    ? () => handleChildCountChange(childCount + 1, 'add')
                    : () => {}
                }
              >
                <PlusIcon />
              </div>
            </div>
          </ul>
        </div>
      </div>
      {childCount > 0 && (
        <div className="childAge-selector-wrapper">
          <p className="latoBold darkText">Child - Age Needed</p>
          <p className="font11 greyText">
            Please select child’s age as on the last day of travel...
            <span
              className="linkText latoBold pointer"
              data-testid="child-age-details-modal"
              onClick={handleChildPolicyModal}
            >
              Know More
            </span>
          </p>
          {!!childCount && (
            <>
              <AddChildrenAge
                ages={children}
                handleChange={handleChildChangeCallback}
                paxConfig={paxConfig}
                bedRequired={childBedRequired}
              />
              {isShowBedRequired && (
                <p className="appendTop25" data-testid="bed-required">
                  <span className="darkText latoBold appendRight30">Bed Required</span>
                  {Object.keys(BED_REQUIRED_ENUM).map((bedOptions, index1) => (
                    <span className="radioOuter makeFlex column" key={index1}>
                      <input
                        type="radio"
                        name={`bed_required${index1}`}
                        value={bedOptions}
                        // onChange={event => }
                        checked={childBedRequired?.[0] === bedOptions}
                      />
                      <label
                        onClick={() => handleBedChangeCallback(bedOptions)}
                        htmlFor={`${bedOptions}${index1}`}
                      >
                        {bedOptions}
                      </label>
                    </span>
                  ))}
                </p>
              )}
              {isExtraBedRequired() && extraBedAllowedMessage && (
                <WarningMessage message={extraBedAllowedMessage} />
              )}
            </>
          )}
        </div>
      )}
      {showModalFlag && <ChildPolicies handleChildPolicyModal={handleChildPolicyModal} />}
    </>
  );
};

export default RoomDetail;
